import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2, User as UserIcon, Trash2, AlertTriangle } from "lucide-react";
import { AvatarUpload } from "@/components/AvatarUpload";
import { logger } from '@/lib/logger';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";
import { useImpersonation } from "@/contexts/ImpersonationContext";

export default function Profile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<{
    full_name: string | null;
    avatar_url: string | null;
    user_id: string;
  } | null>(null);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [userRole, setUserRole] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getEffectiveUserId, isImpersonating, impersonatedUser } = useImpersonation();

  useEffect(() => {
    loadProfile();
  }, [isImpersonating, impersonatedUser?.id]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const effectiveUserId = await getEffectiveUserId();
      
      if (!effectiveUserId) {
        toast({
          title: "Error",
          description: "No user found",
          variant: "destructive",
        });
        return;
      }

      // If impersonating, use impersonated user's email
      if (isImpersonating && impersonatedUser) {
        setEmail(impersonatedUser.email);
      } else {
        // Get email from authenticated user
        const { data: { user } } = await supabase.auth.getUser();
        setEmail(user?.email || "");
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", effectiveUserId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setProfile(data);
        setFullName(data.full_name || "");
      }

      // Fetch user role
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", effectiveUserId)
        .maybeSingle();

      if (roleData) {
        setUserRole(roleData.role);
      }
    } catch (error: any) {
      logger.error("Error loading profile:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Don't allow editing when impersonating
    if (isImpersonating) {
      toast({
        title: "Cannot Edit",
        description: "You cannot edit profiles while impersonating a user",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving(true);
      const effectiveUserId = await getEffectiveUserId();
      
      if (!effectiveUserId) {
        toast({
          title: "Error",
          description: "No user found",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from("profiles")
        .update({ full_name: fullName })
        .eq("user_id", effectiveUserId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });

      await loadProfile();
    } catch (error: any) {
      logger.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText.toLowerCase() !== 'delete my account') {
      toast({
        title: 'Confirmation Required',
        description: 'Please type "delete my account" to confirm',
        variant: 'destructive',
      });
      return;
    }

    setDeleting(true);
    try {
      const { error } = await supabase.functions.invoke('delete-user');

      if (error) throw error;

      toast({
        title: 'Account Deleted',
        description: 'Your account and all associated data have been deleted.',
      });

      // Sign out and redirect to login
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      logger.error('Delete error:', error);
      toast({
        title: 'Deletion Failed',
        description: error instanceof Error ? error.message : 'Failed to delete account',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container max-w-2xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle>Profile Settings</CardTitle>
          <CardDescription>
            Manage your profile information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col items-center space-y-4">
            <Avatar className="h-24 w-24">
              <AvatarImage src={profile?.avatar_url || undefined} />
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                <UserIcon className="h-12 w-12" />
              </AvatarFallback>
            </Avatar>
            <div className="flex gap-2">
              {!isImpersonating && (
                <>
                  <AvatarUpload 
                    currentAvatarUrl={profile?.avatar_url || null}
                    onUploadComplete={(url) => {
                      setProfile(prev => prev ? { ...prev, avatar_url: url } : null);
                      toast({
                        title: "Success",
                        description: "Profile photo updated successfully",
                      });
                    }}
                  />
                  {profile?.avatar_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const effectiveUserId = await getEffectiveUserId();
                          if (!effectiveUserId) return;

                          const { error } = await supabase
                            .from("profiles")
                            .update({ avatar_url: null })
                            .eq("user_id", effectiveUserId);

                          if (error) throw error;

                          setProfile(prev => prev ? { ...prev, avatar_url: null } : null);
                          toast({
                            title: "Success",
                            description: "Profile photo removed",
                          });
                        } catch (error: any) {
                          toast({
                            title: "Error",
                            description: error.message,
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              disabled
              className="bg-muted"
            />
            <p className="text-sm text-muted-foreground">
              Email cannot be changed
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name</Label>
            <Input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
              disabled={isImpersonating}
            />
            {isImpersonating && (
              <p className="text-sm text-muted-foreground">
                Profile editing is disabled while impersonating
              </p>
            )}
          </div>

          {!isImpersonating && (
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="w-full"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive mt-6">
        <CardHeader>
          <CardTitle className="text-destructive flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </CardTitle>
          <CardDescription>
            Irreversible and destructive actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
            className="gap-2"
            disabled={isImpersonating}
          >
            <Trash2 className="h-4 w-4" />
            Delete Account
          </Button>
          {isImpersonating && (
            <p className="text-sm text-muted-foreground mt-2">
              Account deletion is disabled while impersonating
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-2">
            {userRole === 'admin'
              ? "Once you delete your account, there is no going back. All your data, including your team and all North reports, will be permanently deleted."
              : "Once you delete your account, there is no going back. Your profile and personal data will be permanently deleted. Any North reports you created will be transferred to your team admin."
            }
          </p>
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Account Permanently
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p className="font-semibold">
                  This action cannot be undone. This will permanently delete:
                </p>
                {userRole === 'admin' ? (
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Your user account</li>
                    <li>Your entire team</li>
                    <li>All North reports created by you and your team members</li>
                    <li>All showing events and feedback</li>
                    <li>All comparable properties data</li>
                    <li>All social media connections and posts</li>
                    <li>All team subscriptions and billing data</li>
                    <li>Your profile and settings</li>
                  </ul>
                ) : (
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Your user account</li>
                    <li>Your profile and settings</li>
                    <li>Your social media connections</li>
                  </ul>
                )}
                {userRole !== 'admin' && (
                  <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                    <strong>Note:</strong> Any North reports you created will be transferred to your team admin and remain accessible to your team.
                  </p>
                )}
                <div className="space-y-2 pt-4">
                  <Label htmlFor="delete-confirm-text">
                    Type <span className="font-bold">delete my account</span> to confirm
                  </Label>
                  <Input
                    id="delete-confirm-text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="delete my account"
                    className="font-mono"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmText('')}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText.toLowerCase() !== 'delete my account' || deleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete Account Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
